// Header.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AccountInfo } from '@azure/msal-browser';
import styles from './Header.module.css';
import useSession from '@hooks/useSession';

type HeaderProps = {
  readonly showMenu?: boolean;
  readonly account?: (AccountInfo & { name?: string; username?: string }) | null;
  readonly onLogin?: () => void;
  readonly onLogout?: () => void;
};

export default function Header(props: Readonly<HeaderProps>) {
  const { showMenu = true, account, onLogin, onLogout } = props;
  const [menuActive, setMenuActive] = useState(false);
  const [hoveredFlyout, setHoveredFlyout] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const toggleMenu = () => setMenuActive(!menuActive);
  const userName = account?.name ?? account?.username;
  const location = useLocation();
  const { isAdmin, isSuperAdmin, isManager, isUser } = useSession();

  // Convenience role flags
  const canPreparer = isUser || isSuperAdmin;
  const canApproverOrPreparer = isUser || isAdmin || isSuperAdmin;
  // Reporter-only: no Preparer or Approver role
  const isReporterOnly = isManager && !isUser && !isAdmin && !isSuperAdmin;
  const canSeeSpp = isManager || isUser || isAdmin || isSuperAdmin;

  const handleMouseEnter = (key: string) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoveredFlyout(key);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => setHoveredFlyout(null), 150);
    setHoverTimeout(timeout);
  };

  const isFlyoutActive = (flyoutKey: string) => {
    const flyout = flyouts.find((f) => f.key === flyoutKey);
    return flyout?.links.some((link) => location.pathname.startsWith(link.to));
  };

  const flyouts = [
    // FILE UPLOADS — Preparer only
    ...(canPreparer
      ? [
        {
          key: 'file_uploads',
          label: 'FILE UPLOADS',
          links: [{ to: '/file-upload-logs', text: 'File Upload Logs' }],
        },
      ]
      : []),
    // PROD STAT — Preparer-only items filtered; hidden entirely for Reporter
    ...(!isReporterOnly
      ? [
        {
          key: 'prod_stat',
          label: 'PROD STAT',
          links: [
            ...(canApproverOrPreparer ? [{ to: '/estimation-dashboard', text: 'Estimation Factor Dashboard' }] : []),
            ...(canApproverOrPreparer ? [{ to: '/prod-stat-validate-list', text: 'Prod Stat-Prep' }] : []),
            ...(canApproverOrPreparer ? [{ to: '/prod-stat-dashboard', text: 'Prod Stat Dashboard' }] : []),
            ...(canApproverOrPreparer ? [{ to: '/dollar-journal-dashboard', text: 'Dollar Journal' }] : []),
          ],
        },
      ]
      : []),
    // INVOICE — Preparer + Approver only
    ...(canApproverOrPreparer
      ? [
        {
          key: 'invoice',
          label: 'INVOICE',
          links: [{ to: '/invoices-dashboard', text: 'Invoices Dashboard' }],
        },
      ]
      : []),
    // CORE INFO — Preparer + Approver only
    ...(canApproverOrPreparer
      ? [
        {
          key: 'core_info',
          label: 'CORE INFO',
          links: [
            { to: '/nsp-core-info', text: 'NSP Core Info' },
            { to: '/vendor-list', text: 'Vendor list' },
            ...(canApproverOrPreparer ? [{ to: '/audit', text: 'Audit' }] : []),
            ...(canApproverOrPreparer ? [{ to: '/audit-summary', text: 'Audit Summary' }] : []),
            // Aveva is Approver/SuperAdmin only
            ...(isAdmin || isSuperAdmin ? [{ to: '/aveva', text: 'Aveva' }] : []),
          ],
        },
      ]
      : []),
    // SPP REPORTS — Reporter, Preparer, Approver, SuperAdmin
    ...(canSeeSpp
      ? [
        {
          key: 'spp_reports',
          label: 'SPP REPORTS',
          links: [
            { to: '/spp-reports-accounting', text: 'Accounting Report' },
            { to: '/spp-reports-invoice-amounts', text: 'Invoice Amounts Report' },
            { to: '/spp-reports-prod-stat-netgen', text: 'Prod Stat Generation Amounts Report' },
            { to: '/spp-reports-post-prod-stat-netgen', text: 'Post Prod Stat Generation Amounts Report' },
          ],
        },
      ]
      : []),
    // MIGRATION — SuperAdmin only
    ...(isSuperAdmin
      ? [
        {
          key: 'migration',
          label: 'MIGRATION',
          links: [
            { to: '/data-migration', text: 'SPP Data Migration' },
            { to: '/estimates-upload', text: 'Upload Estimates' },
          ],
        },
      ]
      : []),
  ];

  return (
    <header className={styles.header}>
      <div className={styles.topBar} />

      <nav className={styles.mainNav}>
        <div className={styles.container}>
          {/* Logo container */}
          <div className={styles.logoWrapper}>
            <img
              src="/Logo-onwhite.png"
              alt="Nova Scotia Power"
              className={styles.logo}
            />
            <span className={styles.brandTitle}>Windvoice Calculator</span>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={toggleMenu}
            className={styles.mobileToggle}
            aria-label="Toggle navigation menu"
          >
            <span className={styles.mobileBar} />
            <span className={styles.mobileBar} />
            <span className={styles.mobileBar} />
          </button>

          {/* Menu */}
          {showMenu && (
            <div className={styles.flyoutContainer} data-testid="flyout-container" onMouseLeave={handleMouseLeave}>
              {flyouts.map((flyout) => (
                <button
                  key={flyout.key}
                  type="button"
                  className={`${styles.flyout} ${isFlyoutActive(flyout.key) ? styles.active : ''}`}
                  onMouseEnter={() => handleMouseEnter(flyout.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMouseEnter(flyout.key);
                    }
                    if (e.key === 'Escape') {
                      setHoveredFlyout(null);
                    }
                  }}
                  onFocus={() => handleMouseEnter(flyout.key)}
                  onBlur={handleMouseLeave}
                  aria-expanded={hoveredFlyout === flyout.key}
                  aria-haspopup="true"
                  data-testid={`flyout-${flyout.key}`}
                >
                  <span>{flyout.label}</span>

                  <div
                    className={styles.flyoutContent}
                    style={{ display: hoveredFlyout === flyout.key ? 'block' : 'none' }}
                    role="menu"
                  >
                    {flyout.links.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        role="menuitem"
                        className={location.pathname === link.to ? styles.activeLink : ''}
                      >
                        {link.text}
                      </Link>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Login / Logout */}
          {showMenu && (account ? (
            <div className={styles.userBar}>
              <span className={styles.userGreeting}>Hello, {userName}</span>
              <button className={styles.logoutBtn} onClick={onLogout}>
                Logout
              </button>
            </div>
          ) : (
            <button className={styles.loginBtn} onClick={onLogin}>
              MY ACCOUNT LOGIN
            </button>
          ))}
        </div>
      </nav>
    </header>
  );
}
